import React, { useState, useContext } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import "emoji-mart/css/emoji-mart.css";
import { Picker, emojiIndex } from "emoji-mart";
import AddCircleOutlineIcon from '@material-ui/icons/Add';

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import { Menu, MenuItem, MenuList, Grid, Popover, IconButton, makeStyles, useTheme } from "@material-ui/core";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import EditMessageModal from "../EditMessageModal";
import { ForwardMessageContext } from "../../context/ForwarMessage/ForwardMessageContext";
import ForwardModal from "../../components/ForwardMessageModal";
import { toast } from "../../utils/toast";
import toastError from "../../errors/toastError";
import { emojiI18n, setupEmojiMartSearch } from "../../helpers/emojiPtBr";

setupEmojiMartSearch(emojiIndex);

const useStyles = makeStyles((theme) => {
	const dark = theme.palette.type === "dark";
	return {
		iconButton: {
			padding: '4px',
			fontSize: '1.4rem',
			transition: 'transform 0.1s',
			'&:hover': {
				transform: 'scale(1.2)',
			}
		},
		gridContainer: {
			padding: '4px 8px',
			alignItems: 'center',
			justifyContent: 'center',
		},
		quickReactionPaper: {
			backgroundColor: dark ? "#202c33" : "#fff",
			color: dark ? "#e9edef" : "#111b21",
			borderRadius: 50,
			padding: "2px 6px",
			boxShadow: dark ? "0 4px 20px rgba(0,0,0,0.55)" : "0 2px 10px rgba(0,0,0,0.15)",
			border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
		},
		addCircleButton: {
			padding: '6px',
			fontSize: '1.2rem',
			backgroundColor: dark ? "rgba(255,255,255,0.08)" : "rgb(242 242 247)",
			color: dark ? "#8696a0" : "#54656f",
			transition: 'background 0.15s, transform 0.1s',
			'&:hover': {
				backgroundColor: dark ? "rgba(255,255,255,0.15)" : "rgb(230 230 235)",
				transform: 'scale(1.1)',
			}
		},
		emojiBox: {
			borderRadius: 16,
			overflow: "hidden",
			backgroundColor: dark ? "#202c33" : "#ffffff",
			border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
			"& .emoji-mart": {
				border: "none !important",
				borderRadius: 16,
				boxShadow: dark
					? "0 10px 40px rgba(0,0,0,0.6)"
					: "0 10px 32px rgba(0,0,0,0.18)",
				maxWidth: "100%",
				width: "380px !important",
				backgroundColor: "transparent !important",
				color: dark ? "#e9edef" : "#111b21",
			},
			"& .emoji-mart-bar": {
				border: "none !important",
				padding: "6px 10px 2px",
				backgroundColor: "transparent !important",
			},
			"& .emoji-mart-search": {
				padding: "8px 14px 6px",
				position: "relative",
			},
			"& .emoji-mart-search input": {
				backgroundColor: dark ? "#111b21 !important" : "#f0f2f5 !important",
				color: dark ? "#e9edef !important" : "#111b21 !important",
				border: "none !important",
				borderRadius: "8px !important",
				height: "36px",
				padding: "6px 14px 6px 36px !important",
				fontSize: "13.5px",
				outline: "none !important",
			},
			"& .emoji-mart-search-icon": {
				position: "absolute !important",
				left: "24px !important",
				right: "auto !important",
				top: "50% !important",
				transform: "translateY(-50%) !important",
				pointerEvents: "none",
				"& svg": { fill: dark ? "#8696a0" : "#8696a0", width: 16, height: 16 },
			},
			"& .emoji-mart-category-label span": {
				backgroundColor: `${dark ? "#202c33" : "#ffffff"} !important`,
				color: dark ? "#8696a0" : "#667781",
				fontSize: "13px",
				fontWeight: 500,
			},
			"& .emoji-mart-anchor": { color: dark ? "#8696a0" : "#54656f" },
			"& .emoji-mart-anchor-selected": { color: "#00a884 !important" },
			"& .emoji-mart-anchor-bar": {
				backgroundColor: "#00a884 !important",
				height: "3px !important",
				borderRadius: "3px !important",
			},
			"& .emoji-mart-scroll": {
				padding: "0 6px 8px 10px",
				maxHeight: "260px",
				overflowY: "auto",
				"&::-webkit-scrollbar": { width: "6px" },
				"&::-webkit-scrollbar-track": { background: "transparent" },
				"&::-webkit-scrollbar-thumb": {
					backgroundColor: dark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.18)",
					borderRadius: "6px",
				},
			},
			"& .emoji-mart-preview": {
				display: "none !important",
			},
		},
		hideScrollbar: {
			maxHeight: '300px',
			overflow: 'hidden',
		},
	};
});

const MessageOptionsMenu = ({ message, menuOpen, handleClose, anchorEl }) => {
	const classes = useStyles();
	const { setReplyingMessage } = useContext(ReplyMessageContext);
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [confirmationEditOpen, setEditMessageOpenModal] = useState(false);
	const [messageEdit, setMessageEdit] = useState(false);
	const [reactionAnchorEl, setReactionAnchorEl] = useState(null);
	const [moreAnchorEl, setMoreAnchorEl] = useState(null);
	const {
		showSelectMessageCheckbox,
		setShowSelectMessageCheckbox,
		selectedMessages,
		forwardMessageModalOpen,
		setForwardMessageModalOpen } = useContext(ForwardMessageContext);
		

	const handleDeleteMessage = async () => {
		try {
			await api.delete(`/messages/${message.id}`);
		} catch (err) {
			toastError(err);
		}
	};

	const openReactionsMenu = (event) => {
		setReactionAnchorEl(event.currentTarget);
		handleClose();
	};
	
	const closeReactionsMenu = () => {
		setReactionAnchorEl(null);
		handleClose();
	};

	const openMoreReactionsMenu = (event) => {
		setMoreAnchorEl(event.currentTarget);
		closeReactionsMenu();  // Fechar o primeiro popover
	};

	const closeMoreReactionsMenu = () => {
		setMoreAnchorEl(null);
	};

	const handleReactToMessage = async (reactionType) => {
		try {
			await api.post(`/messages/${message.id}/reactions`, { type: reactionType });
			toast.success(i18n.t("messageOptionsMenu.reactionSuccess"));
		} catch (err) {
			toastError(err);
		}
		handleClose();
		closeMoreReactionsMenu(); // Fechar o menu de reações ao reagir
	};

	const theme = useTheme();
	const dark = theme.palette.type === "dark";

	// 6 Emojis rápidos do WhatsApp
    const availableReactions = ['👍', '❤️', '😂', '😮', '😢', '🙏'];	

	const handleSetShowSelectCheckbox = () => {
		setShowSelectMessageCheckbox(!showSelectMessageCheckbox);
		handleClose();
	};

	const handleEditMessage = async () => {
		try {
			await api.put(`/messages/${message.id}`);
		} catch (err) {
			toastError(err);
		}
	}

	const hanldeReplyMessage = () => {
		setReplyingMessage(message);
		handleClose();
	};

	const handleOpenConfirmationModal = e => {
		setConfirmationOpen(true);
		handleClose();
	};

	const handleOpenEditMessageModal = e => {
		setEditMessageOpenModal(true);
		setMessageEdit(message)
		handleClose();
	};

	return (
		<>
			<ForwardModal
			modalOpen={forwardMessageModalOpen}
			messages={selectedMessages}
			onClose={(e) => {
				setForwardMessageModalOpen(false);
				setShowSelectMessageCheckbox(false);
			}}
				/>
			<ConfirmationModal
				title={i18n.t("messageOptionsMenu.confirmationModal.title")}
				open={confirmationOpen}
				onClose={setConfirmationOpen}
				onConfirm={handleDeleteMessage}
			>
				{i18n.t("messageOptionsMenu.confirmationModal.message")}
			</ConfirmationModal>
			<EditMessageModal
				title={i18n.t("messageOptionsMenu.editMessageModal.title")}
				open={confirmationEditOpen}
				onClose={setEditMessageOpenModal}
				onSave={handleEditMessage}
				message={message}
			>
				{i18n.t("messageOptionsMenu.confirmationModal.message")}
			</EditMessageModal>
			<Menu
				anchorEl={anchorEl}
				getContentAnchorEl={null}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				open={menuOpen}
				onClose={handleClose}
			>
				<MenuItem onClick={handleSetShowSelectCheckbox}>
					{i18n.t("messageOptionsMenu.forward")}
				</MenuItem>
				{message.fromMe && (
					<MenuItem onClick={handleOpenEditMessageModal}>
						{i18n.t("messageOptionsMenu.edit")}
					</MenuItem>
				)}
				{message.fromMe && (
					<MenuItem onClick={handleOpenConfirmationModal}>
						{i18n.t("messageOptionsMenu.delete")}
					</MenuItem>
				)}
				<MenuItem onClick={hanldeReplyMessage}>
					{i18n.t("messageOptionsMenu.reply")}
				</MenuItem>
				<MenuItem onClick={openReactionsMenu}>
				{i18n.t("messageOptionsMenu.react")}
				</MenuItem>
			</Menu>
			<Popover
					open={Boolean(reactionAnchorEl)}
					anchorEl={reactionAnchorEl}
					onClose={closeReactionsMenu}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'right',
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'right',
					}}
					PaperProps={{
						className: classes.quickReactionPaper,
						style: { width: 'auto', maxWidth: '380px' }
					}}
				>
					<div className={classes.hideScrollbar}>
						<Grid container spacing={1} className={classes.gridContainer}>
							{availableReactions.map(reaction => (
								<Grid item key={reaction}>
									<IconButton className={classes.iconButton} onClick={() => handleReactToMessage(reaction)}>
										{reaction}
									</IconButton>
								</Grid>
							))}
							<Grid item>
								<IconButton className={classes.addCircleButton} onClick={openMoreReactionsMenu} title="Mais reações">
									<AddCircleOutlineIcon fontSize="small" />
								</IconButton>
							</Grid>
						</Grid>
					</div>
				</Popover>
				<Popover
					open={Boolean(moreAnchorEl)}
					anchorEl={moreAnchorEl}
					onClose={closeMoreReactionsMenu}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'center',
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'center',
					}}
					PaperProps={{
						className: classes.emojiBox,
						style: { width: 'auto', borderRadius: '16px', overflow: 'hidden' }
					}}
				>
					<Picker
						perLine={8}
						showPreview={false}
						showSkinTones={false}
						native
						color="#00a884"
						emojiSize={26}
						title=""
						theme={dark ? "dark" : "light"}
						i18n={emojiI18n}
						onSelect={emoji => {
							handleReactToMessage(emoji.native);
						}}
					/>
				</Popover>
		</>
	);
};

MessageOptionsMenu.propTypes = {
    message: PropTypes.object,
    menuOpen: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    anchorEl: PropTypes.object,
    onReaction: PropTypes.func, // Callback opcional chamado após uma reação
    availableReactions: PropTypes.arrayOf(PropTypes.string) // Lista opcional de reações disponíveis
}

export default MessageOptionsMenu;